import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Modal } from "react-bootstrap";
import { Button } from "../../Controls/Button";
import { HeaderText } from "../../Controls/HeaderText";
import { createApiService } from "../../shared/apiConfig";
import {
  PlateScanApi,
  ServerRequestsPlateScanResponse,
} from "../../shared/api/axios-client";
import { toast } from "react-toastify";
import { Camera, CheckCircle, Clock, ExclamationCircle, XCircle } from "react-bootstrap-icons";

const VIDEO_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: "environment",
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
};

const CROP_RATIO = 0.7;
const JPEG_QUALITY = 0.92;

export interface PlateSessionInfo {
  status: "active" | "expired" | "upcoming" | "not_found";
  /** Pre-formatted start date/time string, e.g. "Mar 31, 9:00 AM" */
  from?: string;
  /** Pre-formatted end date/time string, e.g. "Mar 31, 5:00 PM" */
  to?: string;
}

export interface PlateScannerModalProps {
  show: boolean;
  onHide: () => void;
  /** Return session info for the scanned plate, or null if not found. */
  getPlateSession?: (plate: string) => PlateSessionInfo | null;
}

type Stage = "camera" | "scanning" | "result" | "error";

const sessionResultTheme: Record<PlateSessionInfo["status"], { bg: string; color: string; border: string; icon: React.ReactNode; label: string }> = {
  active:    { bg: "#e9f6df", color: "#2f7d32", border: "#d4ebc4", icon: <CheckCircle size={22} />,       label: "Active session" },
  expired:   { bg: "#f8e8ea", color: "#9f2d2d", border: "#eed2d6", icon: <XCircle size={22} />,           label: "Session expired" },
  upcoming:  { bg: "#dbeafe", color: "#1f5fae", border: "#c7dcf7", icon: <Clock size={22} />,             label: "Upcoming session" },
  not_found: { bg: "#f8efd9", color: "#7a5a18", border: "#eadfbd", icon: <ExclamationCircle size={22} />, label: "No session on file" },
};

export function PlateScannerModal({
  show,
  onHide,
  getPlateSession,
}: PlateScannerModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [stage, setStage] = useState<Stage>("camera");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<ServerRequestsPlateScanResponse | null>(null);
  const plateScanApi = createApiService(PlateScanApi);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    setErrorMessage(null);
    setStage("camera");
    setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Camera access was denied.";
      setErrorMessage(msg);
      setStage("error");
      toast.error("Could not access camera. Please allow camera permission.");
    }
  }, [stopCamera]);

  useEffect(() => {
    if (show) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [show, startCamera, stopCamera]);

  const handleClose = useCallback(() => {
    stopCamera();
    setStage("camera");
    setResult(null);
    setErrorMessage(null);
    onHide();
  }, [onHide, stopCamera]);

  const captureAndScan = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !streamRef.current) return;

    setStage("scanning");
    setErrorMessage(null);

    try {
      const w = video.videoWidth;
      const h = video.videoHeight;
      const cropW = Math.floor(w * CROP_RATIO);
      const cropH = Math.floor(h * CROP_RATIO);
      const x = Math.floor((w - cropW) / 2);
      const y = Math.floor((h - cropH) / 2);

      const canvas = document.createElement("canvas");
      canvas.width = cropW;
      canvas.height = cropH;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not available");
      ctx.drawImage(video, x, y, cropW, cropH, 0, 0, cropW, cropH);

      const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
      const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, "");

      const response = await plateScanApi.scanPlate({
        serverRequestsPlateScanRequest: { image: base64, regionHint: "us" },
      });

      setResult(response.data ?? null);
      setStage("result");
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: string }).message)
          : "Scan failed. Please try again.";
      setErrorMessage(msg);
      setStage("error");
      toast.error("Plate scan failed. Please try again.");
    }
  }, [plateScanApi]);

  const handleRetry = useCallback(() => {
    setResult(null);
    setErrorMessage(null);
    startCamera();
  }, [startCamera]);

  if (!show) return null;

  const scannerBrand = "#e91e8c";
  const cornerBase: React.CSSProperties = {
    position: "absolute",
    width: 22,
    height: 22,
    borderColor: scannerBrand,
    borderStyle: "solid",
    borderWidth: 0,
    pointerEvents: "none",
    zIndex: 2,
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <HeaderText>{stage === "result" ? "Scan result" : "Scan plate"}</HeaderText>
      </Modal.Header>
      <Modal.Body className="p-3">
        <style>{`
          @keyframes captureRipple {
            0%   { transform: translate(-50%, -50%) scale(1);   opacity: 0; }
            8%   { opacity: 0.18; }
            100% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
          }
          .capture-btn-ripple::before,
          .capture-btn-ripple::after {
            content: "";
            position: absolute;
            width: 48px;
            height: 48px;
            left: 50%;
            top: 50%;
            border-radius: 50%;
            background: ${scannerBrand};
            animation: captureRipple 3s ease-out infinite;
          }
          .capture-btn-ripple::after {
            animation-delay: 1.5s;
            animation-fill-mode: backwards;
          }
          @keyframes resultFadeIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          .result-fade-in {
            animation: resultFadeIn 0.5s ease-in-out forwards;
          }
        `}</style>

        {/* ── Camera stage ── */}
        {stage === "camera" && (
          <>
            <div className="position-relative rounded-3 overflow-hidden bg-dark mb-3">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-100"
                style={{ display: "block", maxHeight: "60vh", objectFit: "cover" }}
              />
              {/* Corner brackets */}
              <div style={{ ...cornerBase, top: 10, left: 10, borderTopWidth: 3, borderLeftWidth: 3 }} />
              <div style={{ ...cornerBase, top: 10, right: 10, borderTopWidth: 3, borderRightWidth: 3 }} />
              <div style={{ ...cornerBase, bottom: 10, left: 10, borderBottomWidth: 3, borderLeftWidth: 3 }} />
              <div style={{ ...cornerBase, bottom: 10, right: 10, borderBottomWidth: 3, borderRightWidth: 3 }} />
            </div>

            <p className="text-muted small mb-3 text-center">
              Position the license plate within the frame, then tap to capture
            </p>

            <div className="d-flex justify-content-center mt-2 mb-1">
              <button
                type="button"
                className="capture-btn-ripple border-0 bg-transparent p-0 position-relative d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48 }}
                onClick={captureAndScan}
                aria-label="Capture"
              >
                <span
                  className="position-relative rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 48, height: 48, background: scannerBrand, zIndex: 1 }}
                >
                  <Camera size={20} color="#fff" />
                </span>
              </button>
            </div>
          </>
        )}

        {/* ── Scanning stage ── */}
        {stage === "scanning" && (
          <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 260 }}>
            <p className="text-muted mb-0">Reading plate...</p>
          </div>
        )}

        {/* ── Result stage ── */}
        {stage === "result" && result && (() => {
          const plateStr = (result.plate ?? "").trim();
          const session = plateStr && getPlateSession ? getPlateSession(plateStr) : null;
          const confidencePct = result.confidence != null ? Math.round(result.confidence * 100) : null;
          const confidenceLevel = confidencePct == null ? null : confidencePct >= 90 ? "High" : confidencePct >= 75 ? "Medium" : "Low";

          const status = session?.status ?? "not_found";
          const theme = sessionResultTheme[status];
          const sessionWindow = session?.from && session?.to ? `${session.from} - ${session.to}` : null;
          const statusMessage = status === "active"
            ? session?.to ? `Covered now - ends ${session.to}` : "Covered now"
            : status === "upcoming"
              ? sessionWindow ? `Starts later - ${sessionWindow}` : "Starts later"
              : status === "expired"
                ? session?.to ? `Expired - ended ${session.to}` : "Expired"
                : "Plate not registered for this lot";

          return (
            <div className="result-fade-in">
              {/* Plate */}
              <div className="text-center mb-3 mt-3 rounded-3 border bg-light px-3 py-3">
                <div className="text-muted fw-semibold mb-1" style={{ fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  Scanned plate
                </div>
                <span className="fw-bold" style={{ fontSize: "2rem", letterSpacing: "0.04em", fontFamily: "monospace" }}>
                  {result.plate ?? "—"}
                </span>
              </div>

              <div
                className="d-flex align-items-center gap-3 px-3 py-4 mb-3"
                style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.color }}
              >
                <div className="d-flex align-items-center justify-content-center flex-shrink-0">
                  {theme.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold" style={{ fontSize: "1.15rem" }}>
                    {theme.label}
                  </div>
                  <div className="fw-semibold small">{statusMessage}</div>
                </div>
              </div>

              <div className="row g-3 px-3 pb-3">
                <div className="col-6 col-md-4">
                  <div className="text-muted fw-semibold" style={{ fontSize: "0.65rem" }}>Plate</div>
                  <div className="fw-semibold" style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{plateStr || "-"}</div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="text-muted fw-semibold" style={{ fontSize: "0.65rem" }}>Confidence</div>
                  <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>
                    {confidencePct != null ? `${confidencePct}%` : "-"}
                    {confidenceLevel && (
                      <span className={`ms-2 ${confidenceLevel === "High" ? "text-success" : confidenceLevel === "Medium" ? "text-secondary" : "text-warning"}`}>
                        {confidenceLevel}
                      </span>
                    )}
                  </div>
                </div>
                {sessionWindow && (
                  <div className="col-12 col-md-4">
                    <div className="text-muted fw-semibold" style={{ fontSize: "0.65rem" }}>Window</div>
                    <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>{sessionWindow}</div>
                  </div>
                )}
              </div>

              {/* Low-confidence alert */}
              {confidencePct != null && confidencePct < 75 && (
                <Alert variant="warning" className="mb-3 rounded-3 py-2 small">
                  Results may not be reliable — please review the plate manually before using.
                </Alert>
              )}

              <div className="d-flex justify-content-between gap-2 flex-wrap mt-3">
                <Button type="button" className="rounded-4" onClick={handleClose}>
                  Dismiss
                </Button>
                <Button type="button" className="rounded-4 btn-main" onClick={handleRetry}>
                  Scan again
                </Button>
              </div>
            </div>
          );
        })()}

        {/* ── Error stage ── */}
        {stage === "error" && (
          <>
            <Alert variant="warning" className="mb-3 rounded-3">
              {errorMessage}
            </Alert>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" className="rounded-4" onClick={handleClose}>
                Close
              </Button>
              <Button type="button" className="rounded-4 btn-main" onClick={handleRetry}>
                Try again
              </Button>
            </div>
          </>
        )}

      </Modal.Body>
    </Modal>
  );
}
